import {
  createElement,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
} from 'react';

interface InfiniteScrollProps {
  onLoad: () => void;
  pending?: boolean;
  hasMore?: boolean;
  threshold?: number;
  children: React.ReactNode;
  tagName?: keyof JSX.IntrinsicElements;
  className?: string;
}

export function InfiniteScroll(props: InfiniteScrollProps): ReactElement {
  const {
    tagName = 'div',
    className,
    children,
    pending,
    hasMore,
    threshold = 500,
    onLoad,
  } = props;
  const nodeRef = useRef<HTMLElement>(null);

  const checkScroll = useCallback((): void => {
    if (nodeRef.current == null || pending || !hasMore) {
      return;
    }

    // Check scroll position versus node's height
    const { bottom } = nodeRef.current.getBoundingClientRect();

    if (bottom - threshold < window.innerHeight) {
      onLoad();
    }
  }, [hasMore, onLoad, pending, threshold]);

  useEffect(() => {
    window.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return function cleanup(): void {
      window.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  useEffect(() => {
    checkScroll();
  }, [checkScroll, props]);

  return createElement(
    tagName,
    {
      className,
      ref: nodeRef,
    },
    children,
  );
}
